import ScoringRules from './scoring/ScoringRules';
import React, { useState } from 'react';
import { Button, Input, Modal, Tag, Tooltip } from '@arco-design/web-react';
import MessageReviewDrawer from './MessageReviewDrawer';
import MessageStatsBoard from './MessageStatsBoard';
import { AiPhase, PublicationState, ReviewState } from './types';
import { useMessageManagement } from './useMessageManagement';
import {
  aiColumnLabel,
  aiPhaseColors,
  deriveReviewUiState,
  publicationColumnLabel,
  publicationStateColors,
  reasonHint,
  reviewStateColors,
  reviewStateLabels,
  statusColor
} from './reviewUiState';
import './index.scss';

const aiFilterOptions: [AiPhase | '', string][] = [
  ['', '全部 AI 状态'],
  ['WAITING_INPUT', '等待有效文本'],
  ['PENDING', '待分析'],
  ['RUNNING', '分析中'],
  ['RETRYING', '重试中'],
  ['FAILED', '分析失败'],
  ['COMPLETED', '已完成']
];
const reviewFilterOptions: [ReviewState | '', string][] = [
  ['', '全部人工审核'],
  ['NOT_READY', '暂无需操作'],
  ['NOT_REQUIRED', '无需审核'],
  ['REQUIRED', '需人工审核'],
  ['REVIEWED', '已审核'],
  ['STALE', '需重新审核']
];
const publicationFilterOptions: [PublicationState | '', string][] = [
  ['', '全部公开状态'],
  ['UNPUBLISHED', '未公开'],
  ['PUBLISHING', '发布中'],
  ['PUBLISHED', '已公开'],
  ['WITHDRAWN', '已撤回'],
  ['BLOCKED', '已拦截'],
  ['FAILED', '发布失败']
];

export default function AgentMessageManagement() {
  const [scoringVisible, setScoringVisible] = useState(false);
  const state = useMessageManagement();
  const [q, setQ] = useState('');
  const [aiPhase, setAiPhase] = useState<AiPhase | ''>('');
  const [reviewState, setReviewState] = useState<ReviewState | ''>('');
  const [publicationState, setPublicationState] = useState<PublicationState | ''>('');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [closeConfirmationVisible, setCloseConfirmationVisible] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const { detail, fields } = state;
  const uiInput = {
    detail,
    fields,
    articleDirty: state.articleDirty,
    reasonDirty: state.reasonDirty,
    reviewMismatch: state.reviewMismatch,
    writeBusy: state.writeBusy,
    detailLoading: state.detailLoading,
    detailError: state.detailError,
    receipt: state.receipt,
    retry: state.retry,
    querying: state.querying,
    pollTimedOut: state.pollTimedOut,
    attempted,
    selectedId: state.selectedId,
    conflictPending: state.conflictPending,
    seedSource: state.seedSource,
    lastSavedReviewId: state.lastSavedReviewId,
    requireSaveAfterUnblock: state.requireSaveAfterUnblock
  };
  const ui = deriveReviewUiState(uiInput);
  const selectedItem = state.page?.list.find(item => item.id === state.selectedId);
  const selectedTitle = selectedItem?.title || detail?.review?.title || detail?.text?.title || '没有标题';
  const dirtyToLeave = state.articleDirty || state.reasonDirty;
  const openDetail = (id: string) => {
    setCloseConfirmationVisible(false);
    setAttempted(false);
    setDrawerVisible(true);
    state.setDrawerOpen(true);
    void state.loadDetail(id);
  };
  const hideDrawer = () => {
    setCloseConfirmationVisible(false);
    setDrawerVisible(false);
    state.setDrawerOpen(false);
  };
  const closeDrawer = () => {
    if (dirtyToLeave) {
      setCloseConfirmationVisible(true);
      return;
    }
    hideDrawer();
  };
  const runSave = () => {
    setAttempted(true);
    if (!ui.canSave) return;
    void state.save();
  };
  const runCommand = (type: 'PUBLISH' | 'WITHDRAW' | 'UNBLOCK') => {
    setAttempted(true);
    if (type === 'PUBLISH' && !ui.canPublish) return;
    if (type === 'UNBLOCK' && !ui.canUnblock) return;
    if (type === 'WITHDRAW' && !ui.canWithdraw) return;
    state.command(type);
  };
  return (
    <main className="agent-message-management">
      <ScoringRules visible={scoringVisible} onClose={() => setScoringVisible(false)} />
      <div className="message-management-sticky">
        <MessageStatsBoard stats={state.stats} loading={state.statsLoading} error={state.statsError?.message ?? null} />
        <form
          className="message-toolbar"
          onSubmit={event => {
            event.preventDefault();
            state.submitQuery({ page: 1, size: 20, q, aiPhase, reviewState, publicationState });
          }}
        >
          <Input aria-label="搜索快讯" placeholder="标题或来源 ID" value={q} onChange={setQ} />
          <select aria-label="AI状态" value={aiPhase} onChange={event => setAiPhase(event.target.value as AiPhase | '')}>
            {aiFilterOptions.map(([value, label]) => (
              <option key={value || 'ai-all'} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            aria-label="人工审核"
            value={reviewState}
            onChange={event => setReviewState(event.target.value as ReviewState | '')}
          >
            {reviewFilterOptions.map(([value, label]) => (
              <option key={value || 'review-all'} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            aria-label="公开状态"
            value={publicationState}
            onChange={event => setPublicationState(event.target.value as PublicationState | '')}
          >
            {publicationFilterOptions.map(([value, label]) => (
              <option key={value || 'publication-all'} value={value}>
                {label}
              </option>
            ))}
          </select>
          <Button htmlType="submit" type="primary" disabled={Array.from(q).length > 100}>
            查询快讯
          </Button>
          <Button onClick={() => state.refreshList()}>刷新列表</Button>
          <Button onClick={() => setScoringVisible(true)}>评分规则</Button>
        </form>
      </div>
      {state.listLoading && <p role="status">正在加载快讯…</p>}
      {state.listError && (
        <div role="alert">
            {state.listError.message} <Button onClick={() => state.refreshList()}>重试列表</Button>
        </div>
      )}
      {state.page && (
        <div className="message-list-pane">
          <div className="message-table-scroll">
            <table>
              <thead>
                <tr>
                  <th>快讯</th>
                  <th>来源</th>
                  <th>AI分析</th>
                  <th>人工审核</th>
                  <th>公开状态</th>
                  <th>版本（来源 / 审核）</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {state.page.list.map(item => {
                  const selected = state.selectedId === item.id;
                  return (
                    <tr key={item.id} aria-selected={selected} className={selected ? 'is-selected' : undefined}>
                      <td>
                        {item.title ? (
                          item.title
                        ) : (
                          <span className="message-title-empty">没有标题</span>
                        )}
                      </td>
                      <td>{item.sourceId}</td>
                      <td>
                        <Tooltip content={reasonHint(item.states.ai.reasonCode) || undefined} disabled={!item.states.ai.reasonCode}>
                          <Tag color={statusColor(item.states.ai.phase, aiPhaseColors)}>{aiColumnLabel(item.states.ai)}</Tag>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip content={reasonHint(item.states.review.reasonCode) || undefined} disabled={!item.states.review.reasonCode}>
                          <Tag color={statusColor(item.states.review.state, reviewStateColors)}>
                            {reviewStateLabels[item.states.review.state]}
                          </Tag>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip
                          content={reasonHint(item.states.publication.reasonCode) || undefined}
                          disabled={!item.states.publication.reasonCode}
                        >
                          <Tag color={statusColor(item.states.publication.state, publicationStateColors)}>
                            {publicationColumnLabel(item.states.publication)}
                          </Tag>
                        </Tooltip>
                      </td>
                      <td>
                        {item.sourceVersion} / {item.editorialVersion}
                      </td>
                      <td>
                        <Button
                          aria-label={`查看${item.title || '没有标题的快讯'}`}
                          type={selected && drawerVisible ? 'primary' : 'secondary'}
                          onClick={() => openDetail(item.id)}
                        >
                          {selected && drawerVisible ? '正在查看' : '查看'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!state.page.list.length && <p>暂无匹配快讯</p>}
          <nav aria-label="快讯分页">
            <Button
              disabled={state.query.page <= 1}
              onClick={() => state.setQuery({ ...state.query, page: state.query.page - 1 })}
            >
              上一页
            </Button>
            <span>
              第 {state.query.page} 页 · 共 {state.page.total} 条
            </span>
            <Button
              disabled={state.query.page * state.query.size >= state.page.total}
              onClick={() => state.setQuery({ ...state.query, page: state.query.page + 1 })}
            >
              下一页
            </Button>
          </nav>
        </div>
      )}
      <MessageReviewDrawer
        visible={drawerVisible}
        title={selectedTitle}
        uiInput={uiInput}
        ui={ui}
        actionError={state.actionError}
        notice={state.notice}
        foreignOps={state.foreignOps}
        onClose={closeDrawer}
        onRefresh={state.refreshDetail}
        onRetryDetail={() => state.selectedId && void state.loadDetail(state.selectedId)}
        onRetryCommand={contentId => state.retryCommand(contentId)}
        onRefreshCommand={() => void state.refreshCommand()}
        onSave={runSave}
        onCommand={runCommand}
        onChangeField={state.changeField}
        onAdoptLatest={state.adoptLatestReview}
        onKeepCurrent={state.keepCurrentEdits}
      />
      <Modal
        title={state.articleDirty ? '审核内容尚未保存' : '操作原因尚未提交'}
        visible={closeConfirmationVisible}
        okText="放弃修改并关闭"
        cancelText="继续编辑"
        onOk={hideDrawer}
        onCancel={() => setCloseConfirmationVisible(false)}
        unmountOnExit
      >
        <p>
          {state.articleDirty
            ? '关闭后，本次尚未保存的审核内容会丢失。确定关闭吗？'
            : '关闭后，尚未随保存或发布提交的操作原因会丢失。确定关闭吗？'}
        </p>
      </Modal>
    </main>
  );
}
